import { useEffect, useRef, useState } from 'react';
import { useRecorder } from '../lib/record/useRecorder';
import { RECORDING_PROMPTS } from '../lib/record/prompts';
import './record.css';

function formatTime(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = String(Math.floor(total / 60)).padStart(2, '0');
  const s = String(total % 60).padStart(2, '0');
  return `${m}:${s}`;
}

function IconX() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

export function CaptureView({ onUseTake, onExit }: { onUseTake: (blob: Blob, elapsedMs: number) => void; onExit: () => void }) {
  const rec = useRecorder();
  const liveRef = useRef<HTMLVideoElement>(null);
  const [promptIdx, setPromptIdx] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void rec.requestCamera(); }, []);
  useEffect(() => { if (liveRef.current && rec.stream) liveRef.current.srcObject = rec.stream; }, [rec.stream]);

  if (rec.status === 'denied' || rec.status === 'unsupported' || rec.status === 'error') {
    const copy =
      rec.status === 'denied'
        ? { title: 'We need your camera.', body: 'Legacy Architect records straight from your device. Enable camera access to begin.', cta: 'Enable camera', retry: () => void rec.requestCamera(), cancel: true }
        : rec.status === 'unsupported'
        ? { title: 'This browser can’t record.', body: 'Recording needs a modern browser with camera support. Try Safari or Chrome on your phone.', cta: 'Back to vault', retry: onExit, cancel: false }
        : { title: 'Your recording wasn’t saved.', body: 'Something interrupted the recording. Your moment is safe — try again when you’re ready.', cta: 'Try again', retry: () => void rec.requestCamera(), cancel: true };
    return (
      <div className="rec">
        <div className="rec__recovery la-card">
          <p className="rec__recovery-kicker">Recording</p>
          <h1 className="rec__recovery-title">{copy.title}</h1>
          <p className="rec__recovery-body">{copy.body}</p>
          <div className="la-cta-wrap">
            <button className="la-btn-primary" onClick={copy.retry}>{copy.cta}</button>
          </div>
          {copy.cancel && (
            <button className="la-btn-secondary rec__recovery-cancel" onClick={onExit}>Cancel</button>
          )}
        </div>
      </div>
    );
  }

  if (rec.status === 'recorded' && rec.videoUrl) {
    return (
      <div className="rec">
        <video className="rec__playback" src={rec.videoUrl} controls playsInline />
        <div className="rec__review-actions">
          <button className="la-btn-secondary" onClick={() => rec.retake()}>Retake</button>
          <div className="la-cta-wrap">
            <button className="la-btn-primary" onClick={() => { if (rec.videoBlob) onUseTake(rec.videoBlob, rec.elapsedMs); }}>Use this take</button>
          </div>
        </div>
      </div>
    );
  }

  const recording = rec.status === 'recording';
  const orbDisabled = rec.status === 'requesting' || rec.status === 'idle';
  return (
    <div className="rec">
      <video ref={liveRef} className="rec__live" autoPlay muted playsInline />
      <div className="rec__topbar">
        <button className="rec__icon-btn" aria-label="Close" onClick={onExit}><IconX /></button>
        <span className="rec__timer" role="timer">{recording ? formatTime(rec.elapsedMs) : ''}</span>
        <span className="rec__topbar-spacer" aria-hidden="true" />
      </div>

      <div className="rec__controls">
        <button
          className="rec__record"
          aria-label={recording ? 'Stop recording' : 'Start recording'}
          disabled={orbDisabled}
          onClick={() => (recording ? rec.stop() : rec.start())}
        >
          <span className={recording ? 'rec__record-glyph rec__record-glyph--square' : 'rec__record-glyph'} />
        </button>
      </div>

      <div className="rec__prompt">
        <p className="rec__prompt-kicker">A prompt to begin</p>
        <p className="rec__prompt-text">{RECORDING_PROMPTS[promptIdx]}</p>
        <button type="button" className="rec__prompt-cycle" onClick={() => setPromptIdx((i) => (i + 1) % RECORDING_PROMPTS.length)}>
          New prompt
        </button>
      </div>
    </div>
  );
}
